import type { CartCheckoutGateContext, CartPricingGateContext } from "@/domain/cart/cart-validity.types";

function canPlaceOrderPricing(ctx: CartPricingGateContext): boolean {
  if (!ctx.hasCartItems) return false;
  if (ctx.tier !== "PREVIEW_AUTHORITATIVE") return false;
  if (ctx.freshness !== "fresh") return false;
  if (!ctx.preview) return false;
  if (ctx.fingerprint !== ctx.liveFingerprint) return false;
  // ADR Phase 3: transport memo / pricingStatus settle is insufficient.
  if (!ctx.confirmationEpoch) return false;
  if (ctx.confirmationEpoch !== ctx.liveFingerprint) return false;
  return true;
}

function resolvePricingCatalogFingerprint(ctx: CartPricingGateContext): string | null {
  if (ctx.catalogFingerprint) return ctx.catalogFingerprint;
  try {
    const parsed = JSON.parse(ctx.fingerprint) as { catalogFingerprint?: string };
    return typeof parsed.catalogFingerprint === "string" ? parsed.catalogFingerprint : null;
  } catch {
    return null;
  }
}

/**
 * User may enter checkout and attempt backend preview.
 * Validity must be orderable; pricing may still be syncing.
 */
export function canCheckout(ctx: CartCheckoutGateContext): boolean {
  const { validity, pricing } = ctx;

  if (!pricing.hasCartItems) return false;
  if (validity.status === "UNKNOWN") return false;
  if (validity.status === "RESTAURANT_UNAVAILABLE") return false;
  if (validity.status === "INVALID") return false;

  return true;
}

/**
 * User may submit order / proceed to payment.
 * Requires full validity and Pricing Confirmation for the live pricing epoch
 * (not transport-memo Display alone).
 */
export function canPlaceOrder(ctx: CartCheckoutGateContext): boolean {
  const { validity, pricing } = ctx;

  if (!canCheckout(ctx)) return false;
  if (validity.status !== "VALID") return false;
  if (!canPlaceOrderPricing(pricing)) return false;

  const pricingCatalogFingerprint = resolvePricingCatalogFingerprint(pricing);
  if (
    pricingCatalogFingerprint != null &&
    validity.catalog.fingerprint !== pricingCatalogFingerprint
  ) {
    return false;
  }

  return true;
}

export { canPlaceOrderPricing };
